/**
 * Auth state cache for the SPA.
 *
 * Per the project rules tokens MUST NOT be persisted to localStorage or
 * sessionStorage; we keep the signed-in user purely in JavaScript memory.
 * The session itself lives in the `cc.sid` HttpOnly cookie set by the
 * backend after MSAL completes the Auth Code + PKCE exchange.
 *
 * The public `getUser` / `getToken` / `setUserSession` / `removeUserSession`
 * functions preserve the legacy API surface (most components in this app
 * read `getUser()` synchronously inside render), but they no longer touch
 * `window.localStorage`. The {@link AuthProvider} owns the lifecycle:
 *   - On mount it calls `setUserSession` with whatever `/api/auth/me`
 *     returns.
 *   - On logout it calls `removeUserSession`.
 *
 * `getToken()` returns a stable non-empty sentinel when a user is loaded so
 * the legacy `if (!getToken()) <Navigate to login>` route guards keep
 * working unchanged.
 */
import type { UserDto } from '@cloudcost/shared-types/users';

export interface AuthSessionSnapshot {
  id: number;
  firstName: string;
  username: string;
  default_currency: string;
  is_admin: boolean;
  is_technical: boolean;
  is_business: boolean;
  is_sponsor: boolean;
  is_departmentOwner: boolean;
  is_systemOwner: boolean;
  is_resourceOwner: boolean;
  is_cloudAccountOwner: boolean;
}

type Listener = (snapshot: AuthSessionSnapshot | null) => void;

let current: AuthSessionSnapshot | null = null;
const listeners = new Set<Listener>();

function toSnapshot(user: UserDto | undefined | null): AuthSessionSnapshot | null {
  if (!user || typeof user.id !== 'number') return null;
  return {
    id: user.id,
    firstName: user.firstName ?? '',
    username: user.username ?? '',
    default_currency: user.default_currency ?? 'USD',
    is_admin: !!user.is_admin,
    is_technical: !!user.is_technical,
    is_business: !!user.is_business,
    is_sponsor: !!user.is_sponsor,
    is_departmentOwner: !!user.is_departmentOwner,
    is_systemOwner: !!user.is_systemOwner,
    is_resourceOwner: !!user.is_resourceOwner,
    is_cloudAccountOwner: !!user.is_cloudAccountOwner,
  };
}

function notify(): void {
  for (const listener of listeners) listener(current);
}

export function getUser(): AuthSessionSnapshot | null {
  return current;
}

/**
 * Returns a stable sentinel when the user is authenticated, `null` otherwise.
 * Used by route guards (`if (!getToken()) return <Navigate ...>`). We DO NOT
 * return an actual access/ID/refresh token here — those are never available
 * to client-side JavaScript by design.
 */
export function getToken(): string | null {
  return current ? 'session-cookie' : null;
}

export function isAuthenticated(): boolean {
  return current !== null;
}

export function setUserSession(
  data: UserDto | (Partial<UserDto> & { id: number }) | null,
): void {
  current = toSnapshot(data as UserDto | null);
  notify();
}

export function removeUserSession(): void {
  current = null;
  notify();
}

export function subscribeToSession(listener: Listener): () => void {
  listeners.add(listener);
  listener(current);
  return () => {
    listeners.delete(listener);
  };
}
